import { Controller } from "@hotwired/stimulus"

import { type DispatchEvent } from "@/entry_points/types"
import { isTopmostElement } from "@/helpers/utils"
import { isMobile } from "@/helpers/window_size"

export default class ModalController extends Controller<HTMLDivElement> {
  static targets = ["container"]
  declare readonly containerTarget: HTMLDivElement

  private static readonly FADE_IN_DURATION = 100 // See `--fade-in-duration`
  private static readonly KEYDOWN_EVENT = "keydown"
  private static readonly INITIAL_CLASS = "--initial"
  private static readonly NO_STYLE_CLASS = "no-style"
  private static readonly OPEN_CLASS = "--modal-open"
  private static readonly SMOOTH_SCROLL_CLASS = "scroll-smooth"
  private static readonly SKIP_CLOSE_ANIMATION_CLASS = "--skip-close-animation"

  private shouldAnimate = true
  private closeViaButtonHistory = false
  private scrollYPosition = 0

  initialize(): void {
    this.popStateHandler = this.popStateHandler.bind(this)
    this.beforeCache = this.beforeCache.bind(this)
  }

  connect(): void {
    if (this.isNavigationalModal) {
      window.addEventListener("popstate", this.popStateHandler)
      window.addEventListener("pagehide", this.beforeCache)
    }
  }

  disconnect(): void {
    if (this.isNavigationalModal) {
      window.removeEventListener("popstate", this.popStateHandler)
      window.removeEventListener("pagehide", this.beforeCache)
    }
  }

  animationOff(): void {
    this.shouldAnimate = false
  }

  animationOn(): void {
    this.shouldAnimate = true
  }

  backgroundClose(e: Event): void {
    // Modal spans entire viewport when open, so checking background click means comparing
    // click target to modal itself.
    if (e.target === this.element) {
      this.close()
    }
  }

  close(e?: KeyboardEvent): void {
    if (e && e.type === ModalController.KEYDOWN_EVENT && e.key === undefined) {
      // this ignores artifical keydown events, such as those
      // sent when using browser auto-fill
      return
    }

    if (this.element.hasAttribute("hidden")) {
      return
    }

    if (
      this.isNavigationalModal &&
      history.state?.modalOpen &&
      history.state?.modalId === this.containerId
    ) {
      this.closeViaButtonHistory = true
      history.back()
      return
    }

    this.performClose()
  }

  get containerId(): string {
    return this.containerTarget.id
  }

  open(event?: DispatchEvent<{ modalId?: string }>): void {
    if (event?.detail.modalId && event.detail.modalId !== this.containerId) {
      return
    }

    if (!this.element.hasAttribute("hidden")) {
      return
    }

    this.performOpen()

    if (this.isNavigationalModal) {
      history.pushState({ modalOpen: true, modalId: this.containerId }, "")
    }
  }

  useModalView(): void {
    this.element.classList.remove(ModalController.NO_STYLE_CLASS)
  }

  useNoStyleView(): void {
    document.body.classList.remove(ModalController.OPEN_CLASS)
    this.element.classList.add(ModalController.NO_STYLE_CLASS)
    this.element.removeAttribute("hidden")
  }

  private beforeCache = (): void => {
    // If the modal is a part of browser history
    // it disables animation to prevent the modal from being restored with a stale transform state (cropped slideout)
    this.element.classList.add(ModalController.SKIP_CLOSE_ANIMATION_CLASS)
    this.element.classList.add(ModalController.INITIAL_CLASS)
  }

  private focusInModal = (): void => {
    // To preserve focus within modal (e.g. from tabbing), check if the active element is outside
    // the modal and if so, re-focus the container.
    if (isTopmostElement(this.element) && !this.element.contains(document.activeElement)) {
      this.containerTarget.focus()
    }
  }

  private restoreScrollPosition(): void {
    // Turn off smooth scrolling
    document.documentElement.classList.remove(ModalController.SMOOTH_SCROLL_CLASS)

    // Restore position before modal opened
    window.scrollTo({ top: this.scrollYPosition })

    // Turn smooth scrolling back on
    window.requestAnimationFrame(() => {
      document.documentElement.classList.add(ModalController.SMOOTH_SCROLL_CLASS)
    })
  }

  private popStateHandler(e: PopStateEvent): void {
    const state = e.state as { modalOpen?: boolean; modalId?: string } | null

    if (state?.modalOpen && state.modalId === this.containerId) {
      if (this.element.hasAttribute("hidden")) {
        this.performOpen()
      }

      return
    }

    if (this.element && !this.element.hasAttribute("hidden")) {
      if (this.closeViaButtonHistory) {
        this.closeViaButtonHistory = false
        this.element.classList.remove(ModalController.SKIP_CLOSE_ANIMATION_CLASS)
      } else {
        this.element.classList.add(ModalController.SKIP_CLOSE_ANIMATION_CLASS)
      }

      this.performClose()
    }
  }

  private performOpen(): void {
    this.saveScrollPosition()
    this.setAnimationClass()
    document.body.classList.add(ModalController.OPEN_CLASS)
    document.addEventListener("focus", this.focusInModal, true)
    this.element.removeAttribute("hidden")

    // Focus modal on open (this also ensures `keydown` handler works without user interaction).
    // Elements with CSS `visibility: hidden` aren't focusable, so wait for animation.
    window.setTimeout(() => {
      this.containerTarget.focus()
    }, ModalController.FADE_IN_DURATION)

    document.dispatchEvent(new Event(`${this.identifier}:open`))
  }

  private performClose(): void {
    this.setAnimationClass()
    document.body.classList.remove(ModalController.OPEN_CLASS)
    document.removeEventListener("focus", this.focusInModal, true)
    this.element.setAttribute("hidden", "until-found")
    this.restoreScrollPosition()

    this.dispatch("closeModal")
  }

  private saveScrollPosition(): void {
    // CSS applies `position: fixed` when modal opens because iOS reasons. Doing so means we lose
    // page scroll position, so keep track of it to restore it on modal close.
    this.scrollYPosition = Math.max(0, window.scrollY)
    document.body.style.setProperty("--scroll-y-position", `${this.scrollYPosition}px`)
  }

  private setAnimationClass(): void {
    this.element.classList.toggle(ModalController.INITIAL_CLASS, !this.shouldAnimate)
  }

  private get isNavigationalModal(): boolean {
    return this.element.classList.contains("reveal:slide-left") && isMobile()
  }
}
